<template>
  <a-modal
    :open="visible"
    title="出参结构变更"
    :maskClosable="false"
    :closable="false"
    width="500px"
    @ok="handleConfirm"
    @cancel="handleCancel"
  >
    <div class="version-change-content">
      <a-alert
        message="检测到出参结构发生变化"
        description="出参结构变更相当于接口文档变更，请选择版本升级类型并填写变更说明。"
        type="warning"
        show-icon
        style="margin-bottom: 16px;"
      />

      <a-form layout="vertical">
        <a-form-item label="版本升级类型" required>
          <a-radio-group v-model:value="formData.versionType" class="version-type-group">
            <a-radio-button value="patch">
              <div class="version-type-option">
                <span class="version-label">修订 Z+1</span>
                <span class="version-desc">描述/约束调整</span>
              </div>
            </a-radio-button>
            <a-radio-button value="minor">
              <div class="version-type-option">
                <span class="version-label">次版本 Y+1</span>
                <span class="version-desc">新增字段</span>
              </div>
            </a-radio-button>
            <a-radio-button value="major">
              <div class="version-type-option">
                <span class="version-label">主版本 X+1</span>
                <span class="version-desc">删除/重命名</span>
              </div>
            </a-radio-button>
          </a-radio-group>
        </a-form-item>

        <a-form-item label="变更说明">
          <a-textarea
            v-model:value="formData.changeDescription"
            placeholder="请简要描述本次变更内容，例如：新增 xxx 字段用于..."
            :rows="3"
            :maxlength="200"
            show-count
          />
        </a-form-item>
      </a-form>

      <div class="version-preview">
        <span>版本变化：</span>
        <a-tag color="blue">{{ currentVersion }}</a-tag>
        <span>→</span>
        <a-tag color="green">{{ newVersion }}</a-tag>
      </div>
    </div>

    <template #footer>
      <a-button @click="handleSkip">跳过版本记录</a-button>
      <a-button @click="handleCancel">取消保存</a-button>
      <a-button type="primary" @click="handleConfirm">确认变更</a-button>
    </template>
  </a-modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  currentVersion: {
    type: String,
    default: 'v1.0.0'
  }
});

const emit = defineEmits(['update:visible', 'confirm', 'skip', 'cancel']);

const formData = ref({
  versionType: 'patch',
  changeDescription: ''
});

// 计算新版本号
const newVersion = computed(() => {
  const match = props.currentVersion.match(/^v?(\d+)\.(\d+)\.(\d+)$/);
  if (!match) return 'v1.0.1';
  
  let [, major, minor, patch] = match.map(Number);
  
  switch (formData.value.versionType) {
    case 'major':
      return `v${major + 1}.0.0`;
    case 'minor':
      return `v${major}.${minor + 1}.0`;
    case 'patch':
    default:
      return `v${major}.${minor}.${patch + 1}`;
  }
});

// 确认变更
const handleConfirm = () => {
  emit('confirm', {
    versionType: formData.value.versionType,
    changeDescription: formData.value.changeDescription,
    newVersion: newVersion.value
  });
  emit('update:visible', false);
};

// 跳过版本记录
const handleSkip = () => {
  emit('skip');
  emit('update:visible', false);
};

// 取消
const handleCancel = () => {
  emit('cancel');
  emit('update:visible', false);
};

// 重置表单
watch(() => props.visible, (val) => {
  if (val) {
    formData.value = {
      versionType: 'patch',
      changeDescription: ''
    };
  }
});
</script>

<style scoped>
.version-change-content {
  padding: 8px 0;
}

.version-type-group {
  display: flex;
  width: 100%;
}

.version-type-group :deep(.ant-radio-button-wrapper) {
  flex: 1;
  height: auto;
  padding: 6px 8px;
  text-align: center;
}

.version-type-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2px 0;
}

.version-label {
  font-weight: 500;
  font-size: 13px;
  white-space: nowrap;
}

.version-desc {
  font-size: 10px;
  color: #999;
  margin-top: 2px;
  white-space: nowrap;
}

.version-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-top: 8px;
}

.version-preview span {
  color: #666;
}
</style>

